import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import {
  downloadTemplate,
  removeUploadedFile,
  uploadFileForParsing
} from 'modules/create-new-profiles/reducers/actions';
import { CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER } from 'modules/create-new-profiles/constants/common';

import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';

import './create-new-profiles-upload-form-controls.scss';

applyTranslations(require('./create-new-profiles-upload-form-controls.i18n.json'));

import { State } from 'types';

interface StateProps {
  filename: string | null
}

interface DispatchProps {
  dispatchDownloadTemplate: () => void,
  dispatchUploadFile: (file: File | null) => void,
  dispatchRemoveFile : () => void
}

type Props = StateProps & DispatchProps;

const CreateNewProfilesUploadFormControls = ({
  filename,
  dispatchDownloadTemplate,
  dispatchUploadFile,
  dispatchRemoveFile
}: Props): JSX.Element => {
  const fileUploadRef = React.createRef<HTMLInputElement>();

  return (
    <div className="create-new-profiles-upload-form-controls">
      <div className="create-new-profiles-upload-form-controls__header">
        {__t('create_new_profiles_upload_form_controls.header')}
      </div>
      <div>
        <input
          ref={fileUploadRef}
          onClick={event => {
            try {
              // @ts-ignore Because it's a hack to make possible try to upload same file (for example after an error)
              event.target.value = null; // eslint-disable-line no-param-reassign
            } catch (error) {
              logError(error);
            }
          }}
          onChange={event => {
            const { target } = event;
            const { files } = target;
            const file = (files ?? [])[0] ?? null;

            dispatchUploadFile(file);
          }}
          className="messenger-actions__file"
          type="file"
          accept="text/xlsx"
        />
        <ActionButton
          color="tertiary"
          onClick={dispatchDownloadTemplate}
        >
          {__t('create_new_profiles_upload_form_controls.buttons.download')}
        </ActionButton>
        <ActionButton
          onClick={() => {
            if (fileUploadRef.current) {
              fileUploadRef.current.click();
            }
          }}
        >
          {__t('create_new_profiles_upload_form_controls.buttons.upload')}
        </ActionButton>
      </div>
      <span className="create-new-profiles-upload-form-controls__subheader">
        {__t('create_new_profiles_upload_form_controls.subheader')}
      </span>
      <div className="create-new-profiles-upload-form-controls__file-info">
        {filename === null ? (
          <span className="create-new-profiles-upload-form-controls__no-filename">
            {__t('create_new_profiles_upload_form_controls.no_file_placeholder')}
          </span>
        ) : (
          <React.Fragment>
            <span className="create-new-profiles-upload-form-controls__filename">
              {filename}
            </span>
            <div>
              <Tooltip
                width="macro"
                message={__t('create_new_profiles_upload_form_controls.tooltip_text')}
              >
                <SVGDynamicIcon
                  name="cross-custom-fg"
                  width={12}
                  height={12}
                  customColor="grey"
                  onClick={dispatchRemoveFile}
                />
              </Tooltip>
            </div>
          </React.Fragment>
        )}
      </div>
      <div className="create-new-profiles-upload-form-controls__error">
        {filename !== null ? (
          __t('create_new_profiles_upload_form_controls.error_message')
        ) : null}
      </div>

    </div>
  );
};

const mapStateToProps = ({ createNewProfiles }: State): StateProps => {
  const { createEntitiesModal } = createNewProfiles;

  if (createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
    throw new Error(`CreateNewProfilesUploadFormControls component is not available for ${CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER} state!`);
  }

  return {
    filename: createEntitiesModal.data?.type === 'unsuccessful-upload'
      ? createEntitiesModal.data.filename
      : null
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadTemplate: () => dispatch(downloadTemplate()),
  dispatchUploadFile: file => dispatch(uploadFileForParsing(file)),
  dispatchRemoveFile: () => dispatch(removeUploadedFile())
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateNewProfilesUploadFormControls);
